import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ROUTES } from "../../constants/routes";
import { submitInquiry } from "../../utils/submitInquiry";
import { trackFormSubmit } from "../../utils/analytics";
import MotionReveal from "../common/MotionReveal/MotionReveal";
import { homePartnerContent } from "../../data/homeData";
import { Paperclip } from "lucide-react";

function HomePartnerInquiry() {
  const navigate = useNavigate();
  const data = homePartnerContent;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    attachment: null,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Full Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email Address is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Contact Number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit contact number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company Name is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Requirement details are required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) return;
  
    setSubmitting(true);
    try {
      await submitInquiry("home-quick-inquiry", formData);
      trackFormSubmit("home-quick-inquiry");
  
      alert("Your inquiry has been submitted successfully!");
  
      // Optional: Clear the form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
        attachment: null,
      });
    } catch {
      setErrors({ form: "Unable to submit. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-slate-50 py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <MotionReveal variant="fadeUp" as="span" className="uppercase tracking-[0.3em] text-red-600 text-sm font-semibold">
              {data.eyebrow}
            </MotionReveal>

            <MotionReveal variant="fadeUp" delay={100} as="h2" className="mt-5 text-5xl font-bold text-slate-900">
              {data.title}
              <span className="text-red-500"> {data.titleAccent}</span>
            </MotionReveal>

            <div className="mt-10 space-y-6">
              {data.cards.map((item, index) => (
                <MotionReveal
                  variant="fadeRight"
                  delay={index * 100}
                  className="bg-white rounded-[28px] p-8 shadow-lg hover:-translate-y-2 hover:shadow-2xl transition-all duration-300"
                  key={item.title}
                >
                  <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-3 text-slate-600">{item.description}</p>
                </MotionReveal>
              ))}
            </div>
          </div>

          <MotionReveal
            variant="fadeLeft"
            className="bg-[#041E42] rounded-[32px] p-10 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-72 h-72 bg-red-500/10 blur-[100px]" />

            <div className="relative">
              <span className="uppercase tracking-[0.3em] text-red-400 text-sm font-semibold">
                {data.formEyebrow}
              </span>

              <h3 className="text-4xl font-bold mt-4">{data.formTitle}</h3>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label htmlFor="home-inquiry-name" className="sr-only">
                    Full Name
                  </label>
                  <input
                    id="home-inquiry-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className={`w-full px-5 py-4 rounded-xl bg-white/10 outline-none border transition-all ${
                      errors.name ? "border-red-500" : "border-white/10"
                    }`}
                  />
                  {errors.name && <p className="text-red-400 text-sm mt-2">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="home-inquiry-email" className="sr-only">
                    Email Address
                  </label>
                  <input
                    id="home-inquiry-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className={`w-full px-5 py-4 rounded-xl bg-white/10 outline-none border transition-all ${
                      errors.email ? "border-red-500" : "border-white/10"
                    }`}
                  />
                  {errors.email && <p className="text-red-400 text-sm mt-2">{errors.email}</p>}
                </div>
                <div>
  <label htmlFor="home-inquiry-phone" className="sr-only">
    Contact Number
  </label>

  <input
    id="home-inquiry-phone"
    type="tel"
    name="phone"
    value={formData.phone}
    onChange={handleChange}
    placeholder="Contact Number"
    maxLength={10}
    className={`w-full px-5 py-4 rounded-xl bg-white/10 outline-none border transition-all ${
      errors.phone ? "border-red-500" : "border-white/10"
    }`}
  />

  {errors.phone && (
    <p className="text-red-400 text-sm mt-2">
      {errors.phone}
    </p>
  )}
</div>

                <div>
                  <label htmlFor="home-inquiry-company" className="sr-only">
                    Company Name
                  </label>
                  <input
                    id="home-inquiry-company"
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company Name"
                    className={`w-full px-5 py-4 rounded-xl bg-white/10 outline-none border transition-all ${
                      errors.company ? "border-red-500" : "border-white/10"
                    }`}
                  />
                  {errors.company && <p className="text-red-400 text-sm mt-2">{errors.company}</p>}
                </div>

                <div>
  <label htmlFor="home-inquiry-message" className="sr-only">
    Requirement details
  </label>

  {/* Textarea + Icon */}
  <div className="relative">
    <textarea
      id="home-inquiry-message"
      rows="5"
      name="message"
      value={formData.message}
      onChange={handleChange}
      placeholder="Tell us about your requirement"
      className={`w-full px-5 py-4 pr-14 rounded-xl bg-white/10 outline-none border resize-none transition-all ${
        errors.message ? "border-red-500" : "border-white/10"
      }`}
    />

    <input
      type="file"
      id="attachment"
      className="hidden"
      onChange={(e) =>
        setFormData((prev) => ({
          ...prev,
          attachment: e.target.files[0],
        }))
      }
    />

    <label
      htmlFor="attachment"
      className="absolute bottom-4 right-4 cursor-pointer text-white hover:text-red-400"
    >
      <Paperclip size={22} />
    </label>
  </div>

  {/* File name BELOW the textarea */}
  {formData.attachment && (
    <p className="mt-2 text-xs text-slate-300 truncate">
      {formData.attachment.name}
    </p>
  )}

  {errors.message && (
    <p className="text-red-400 text-sm mt-2">
      {errors.message}
    </p>
  )}
</div>

                {errors.form && <p className="text-red-400 text-sm">{errors.form}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative overflow-hidden rounded-full bg-red-600 px-8 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="relative z-10">{data.formButton}</span>
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-700 group-hover:translate-x-full" />
                </button>
              </form>
            </div>
          </MotionReveal>
        </div>
      </div>
    </section>
  );
}

export default HomePartnerInquiry;
